// Multi-select pickers shared by the Opportunities filters and Pipeline
// Configuration, so the criteria a user can search by and the criteria the
// matcher runs on are the same lists with the same names.
import React, { useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon } from 'components/icons';

export const SET_ASIDE_TYPES = [
  { code: 'SBA', label: 'Total Small Business Set-Aside' },
  { code: 'SBP', label: 'Partial Small Business Set-Aside' },
  { code: '8A', label: '8(a) Set-Aside' },
  { code: '8AN', label: '8(a) Sole Source' },
  { code: 'HZC', label: 'HUBZone Set-Aside' },
  { code: 'HZS', label: 'HUBZone Sole Source' },
  { code: 'SDVOSBC', label: 'SDVOSB Set-Aside' },
  { code: 'SDVOSBS', label: 'SDVOSB Sole Source' },
  { code: 'WOSB', label: 'WOSB Set-Aside' },
  { code: 'WOSBSS', label: 'WOSB Sole Source' },
  { code: 'EDWOSB', label: 'EDWOSB Set-Aside' },
  { code: 'EDWOSBSS', label: 'EDWOSB Sole Source' },
  { code: 'LAS', label: 'Local Area Set-Aside' },
  { code: 'IEE', label: 'Indian Economic Enterprise' },
  { code: 'ISBEE', label: 'Indian Small Business Economic Enterprise' },
  { code: 'BICiv', label: 'Buy Indian Set-Aside' },
  { code: 'VSA', label: 'VA Veteran-Owned Set-Aside' },
  { code: 'VSS', label: 'VA Veteran-Owned Sole Source' },
];

export const US_STATES = [
  { code: 'AL', label: 'Alabama' }, { code: 'AK', label: 'Alaska' }, { code: 'AZ', label: 'Arizona' },
  { code: 'AR', label: 'Arkansas' }, { code: 'CA', label: 'California' }, { code: 'CO', label: 'Colorado' },
  { code: 'CT', label: 'Connecticut' }, { code: 'DE', label: 'Delaware' }, { code: 'DC', label: 'District of Columbia' },
  { code: 'FL', label: 'Florida' }, { code: 'GA', label: 'Georgia' }, { code: 'HI', label: 'Hawaii' },
  { code: 'ID', label: 'Idaho' }, { code: 'IL', label: 'Illinois' }, { code: 'IN', label: 'Indiana' },
  { code: 'IA', label: 'Iowa' }, { code: 'KS', label: 'Kansas' }, { code: 'KY', label: 'Kentucky' },
  { code: 'LA', label: 'Louisiana' }, { code: 'ME', label: 'Maine' }, { code: 'MD', label: 'Maryland' },
  { code: 'MA', label: 'Massachusetts' }, { code: 'MI', label: 'Michigan' }, { code: 'MN', label: 'Minnesota' },
  { code: 'MS', label: 'Mississippi' }, { code: 'MO', label: 'Missouri' }, { code: 'MT', label: 'Montana' },
  { code: 'NE', label: 'Nebraska' }, { code: 'NV', label: 'Nevada' }, { code: 'NH', label: 'New Hampshire' },
  { code: 'NJ', label: 'New Jersey' }, { code: 'NM', label: 'New Mexico' }, { code: 'NY', label: 'New York' },
  { code: 'NC', label: 'North Carolina' }, { code: 'ND', label: 'North Dakota' }, { code: 'OH', label: 'Ohio' },
  { code: 'OK', label: 'Oklahoma' }, { code: 'OR', label: 'Oregon' }, { code: 'PA', label: 'Pennsylvania' },
  { code: 'RI', label: 'Rhode Island' }, { code: 'SC', label: 'South Carolina' }, { code: 'SD', label: 'South Dakota' },
  { code: 'TN', label: 'Tennessee' }, { code: 'TX', label: 'Texas' }, { code: 'UT', label: 'Utah' },
  { code: 'VT', label: 'Vermont' }, { code: 'VA', label: 'Virginia' }, { code: 'WA', label: 'Washington' },
  { code: 'WV', label: 'West Virginia' }, { code: 'WI', label: 'Wisconsin' }, { code: 'WY', label: 'Wyoming' },
  { code: 'AS', label: 'American Samoa' }, { code: 'GU', label: 'Guam' }, { code: 'MP', label: 'Northern Mariana Islands' },
  { code: 'PR', label: 'Puerto Rico' }, { code: 'VI', label: 'U.S. Virgin Islands' },
];

export const toggleIn = (list: string[], code: string): string[] =>
  list.includes(code) ? list.filter(c => c !== code) : [...list, code];

/** A set of checkboxes for a multi-select filter. */
export function CheckList({
  labelId,
  options,
  selected,
  onChange,
  disabled = false,
  twoColumn = false,
  scroll = false,
}: {
  labelId: string;
  options: { code: string; label: string }[];
  selected: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  twoColumn?: boolean;
  scroll?: boolean;
}) {
  return (
    <div
      role="group"
      aria-labelledby={labelId}
      className={`border border-border rounded-lg p-2 grid gap-x-3 gap-y-1 ${twoColumn ? 'grid-cols-2' : 'grid-cols-1'} ${scroll ? 'max-h-52 overflow-y-auto' : ''} ${disabled ? 'opacity-60' : ''}`}
    >
      {options.map(o => (
        <label key={o.code} className={`flex items-center gap-2 text-sm py-0.5 ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
          <input
            type="checkbox"
            checked={selected.includes(o.code)}
            onChange={() => onChange(toggleIn(selected, o.code))}
            disabled={disabled}
            className="h-4 w-4 rounded border-border-strong text-accent focus:ring-accent"
          />
          <span className="text-content">{o.label}</span>
        </label>
      ))}
    </div>
  );
}

export interface AgencyOption { name: string; openCount: number; subTiers: { name: string; openCount: number }[] }

/** A selection value: a department name, or "DEPT.SUBTIER" for one of its sub-tiers (SAM's own path shape). */
const agencyPath = (dept: string, sub: string) => `${dept}.${sub}`;
/** Human form of a selection value, for chips: "DEPT OF DEFENSE › DEPT OF THE NAVY". */
export const agencyLabel = (value: string) => value.replace('.', ' › ');
const openText = (n: number) => (n ? `${n.toLocaleString()} open` : 'none open');

/**
 * Department list with expandable sub-tiers. Ticking a department covers all
 * of it (its sub-tiers show ticked and locked); ticking sub-tiers alone marks
 * the department as partially selected. Chosen departments stay pinned at the
 * top, and the search box matches sub-tier names too, opening their parent.
 */
export function AgencyTree({ labelId, options, selected, onChange, search }: {
  labelId: string;
  options: AgencyOption[];
  selected: string[];
  onChange: (next: string[]) => void;
  search: string;
}) {
  const [expanded, setExpanded] = useState<string[]>([]);
  const q = search.trim().toLowerCase();
  const matches = (name: string) => !q || name.toLowerCase().includes(q);

  const rows = options.map(dept => {
    const deptChecked = selected.includes(dept.name);
    const subsChecked = dept.subTiers.filter(st => selected.includes(agencyPath(dept.name, st.name)));
    const subMatches = dept.subTiers.filter(st => matches(st.name) || selected.includes(agencyPath(dept.name, st.name)));
    const pinned = deptChecked || subsChecked.length > 0;
    const deptMatches = matches(dept.name);
    return { dept, deptChecked, subsChecked, subMatches, pinned, deptMatches, visible: pinned || deptMatches || subMatches.length > 0 };
  }).filter(r => r.visible);
  const ordered = [...rows.filter(r => r.pinned), ...rows.filter(r => !r.pinned)];

  const toggleDept = (dept: AgencyOption) => {
    const own = dept.subTiers.map(st => agencyPath(dept.name, st.name));
    onChange(selected.includes(dept.name)
      ? selected.filter(v => v !== dept.name)
      // Whole department now covers every sub-tier, so drop the partial picks.
      : [...selected.filter(v => !own.includes(v)), dept.name]);
  };

  return (
    <div role="group" aria-labelledby={labelId} className="border border-border rounded-lg p-2 max-h-64 overflow-y-auto">
      {ordered.length === 0 && <p className="text-caption text-content-muted px-1 py-2">No agencies match “{search.trim()}”.</p>}
      {ordered.map(({ dept, deptChecked, subsChecked, subMatches, deptMatches }) => {
        const hasSubs = dept.subTiers.length > 0;
        // Open when the user opened it, when a sub-tier is picked, or when only a sub-tier matched the search.
        const isOpen = hasSubs && (expanded.includes(dept.name) || subsChecked.length > 0 || (!!q && !deptMatches));
        const shownSubs = q && !deptMatches ? subMatches : dept.subTiers;
        return (
          <div key={dept.name}>
            <div className="flex items-center gap-1 py-0.5">
              {hasSubs ? (
                <button
                  type="button"
                  onClick={() => setExpanded(toggleIn(expanded, dept.name))}
                  aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${dept.name}`}
                  aria-expanded={isOpen}
                  className="p-0.5 rounded text-content-muted hover:text-content"
                >
                  {isOpen ? <ChevronDownIcon className="h-3.5 w-3.5" /> : <ChevronRightIcon className="h-3.5 w-3.5" />}
                </button>
              ) : <span className="w-[18px]" aria-hidden="true" />}
              <label className="flex items-center gap-2 text-sm cursor-pointer min-w-0">
                <input
                  type="checkbox"
                  checked={deptChecked}
                  ref={el => { if (el) el.indeterminate = !deptChecked && subsChecked.length > 0; }}
                  onChange={() => toggleDept(dept)}
                  className="h-4 w-4 rounded border-border-strong text-accent focus:ring-accent"
                />
                <span className="text-content truncate">{dept.name} · {openText(dept.openCount)}</span>
                {!deptChecked && subsChecked.length > 0 && (
                  <span className="text-caption text-content-muted whitespace-nowrap">{subsChecked.length} of {dept.subTiers.length}</span>
                )}
              </label>
            </div>
            {isOpen && (
              <div className="ml-7 border-l border-border pl-2">
                {shownSubs.map(st => {
                  const value = agencyPath(dept.name, st.name);
                  return (
                    <label key={value} className={`flex items-center gap-2 text-sm py-0.5 ${deptChecked ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`} title={deptChecked ? 'Whole department selected' : undefined}>
                      <input
                        type="checkbox"
                        checked={deptChecked || selected.includes(value)}
                        disabled={deptChecked}
                        onChange={() => onChange(toggleIn(selected, value))}
                        className="h-4 w-4 rounded border-border-strong text-accent focus:ring-accent"
                      />
                      <span className="text-content truncate">{st.name} · {openText(st.openCount)}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Label row for a multi-select: the field name, how many are picked, and a way to clear them. */
export function MultiLabel({ id, text, count, onClear, emptyText }: { id: string; text: string; count: number; onClear: () => void; emptyText: string }) {
  return (
    <div className="flex items-baseline justify-between mb-1.5">
      <span id={id} className="label">{text}</span>
      {count > 0 ? (
        <button type="button" onClick={onClear} className="text-caption text-content-muted hover:text-content underline">
          Clear {count} selected
        </button>
      ) : (
        <span className="text-caption text-content-subtle">{emptyText}</span>
      )}
    </div>
  );
}
